import { BalanceImpact, CurrencyAmount, OrderSide, PerpPositionAction, PositionSide, notEmpty } from "@orb-labs/types";
import { formatUsd } from "../../bullrun/price.js";
import { isPerpTrade, isPredictionTradeItem, isTokenTrade } from "../../hooks/bullrunQueries/tradeItemGuards.js";
import { SignalFeedItemKind, } from "../../hooks/bullrunQueries/signalFeedItems.js";
/** "Opened" / "Added to" / "Reduced" / "Closed" / "Flipped to" — verb per action. */
const PERP_ACTION_VERB = {
    [PerpPositionAction.Open]: "Opened",
    [PerpPositionAction.Increase]: "Added to",
    [PerpPositionAction.Reduce]: "Reduced",
    [PerpPositionAction.Close]: "Closed",
    [PerpPositionAction.Flip]: "Flipped to",
};
/** Actions on which the venue-reported realized PnL is rendered. */
const PNL_BEARING_ACTIONS = new Set([
    PerpPositionAction.Reduce,
    PerpPositionAction.Close,
    PerpPositionAction.Flip,
]);
/** "12s" / "4m" / "3h" / "2d" since `ms`; empty string when undated. */
export function relativeTime(ms) {
    if (!notEmpty(ms) || !(ms > 0))
        return "";
    const seconds = Math.max(0, Math.floor((Date.now() - ms) / 1000));
    if (seconds < 60)
        return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60)
        return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
}
/** Raw USDC-units bigint from a CurrencyAmount instance, or 0 on undefined. */
export function rawUsd(amount) {
    if (!notEmpty(amount))
        return BigInt(0);
    try {
        return amount.toRawAmount();
    }
    catch {
        return BigInt(0);
    }
}
/** Raw USDC-units bigint from a decimal string ("31.4"), or 0. */
export function rawUsdFromDecimal(decimal) {
    if (!notEmpty(decimal))
        return BigInt(0);
    return CurrencyAmount.parseDecimalToScaledBigInt(decimal, 6) ?? BigInt(0);
}
/**
 * "3.2× usual size" — the server only stamps a multiple on algorithmic
 * rows with a sufficient baseline; surfaced when it's notable (>= 2×).
 */
export function formatConvictionLabel(multiple) {
    if (!notEmpty(multiple) || multiple < 2)
        return undefined;
    return `${multiple >= 10 ? Math.round(multiple) : multiple.toFixed(1)}× usual size`;
}
/**
 * "+$12.4k PnL" / "-$870 PnL" from a signed USD CurrencyAmount. A
 * break-even close renders "+$0.00 PnL" — the server only attaches
 * closedPnl on PnL-bearing actions, where $0 is a real datum.
 */
function formatSignedPnl(closedPnl) {
    if (!notEmpty(closedPnl))
        return undefined;
    const raw = rawUsd(closedPnl);
    const sign = raw < BigInt(0) ? "-" : "+";
    const abs = raw < BigInt(0) ? -raw : raw;
    return `${sign}${formatUsd(abs)} PnL`;
}
/** Epoch ms from a wire timestamp; undefined when missing or unparseable. */
function timestampToMs(value) {
    if (!notEmpty(value))
        return undefined;
    const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return Number.isFinite(ms) ? ms : undefined;
}
/**
 * Perp row summary. Rows without position context (realtime tape rows,
 * rows predating migration 0061) keep the bare BUY/SELL rendering; rows
 * with context upgrade the chip to "<verb> LONG/SHORT" and append the
 * venue-reported realized PnL when present.
 */
function summarizePerpTrade(item) {
    const direction = item.positionDirection;
    const action = item.positionAction;
    const hasContext = notEmpty(direction) && notEmpty(action);
    const directionLabel = direction === PositionSide.Long ? "LONG" : "SHORT";
    // PnL only renders on PnL-bearing actions (mirrors the server gate) so an
    // "Opened LONG" row can never pick up a stray closedPnl tail.
    const pnlBearing = hasContext && PNL_BEARING_ACTIONS.has(action);
    return {
        isBuy: item.side === OrderSide.BUY,
        timestampMs: timestampToMs(item.timestamp),
        formattedTradeUsd: formatUsd(rawUsd(item.value)),
        perpChipLabel: hasContext ? `${PERP_ACTION_VERB[action]} ${directionLabel}` : undefined,
        perpChipIsLong: hasContext ? direction === PositionSide.Long : undefined,
        perpTail: pnlBearing ? formatSignedPnl(item.closedPnl) : undefined,
    };
}
/**
 * Discriminated read of the SDK trade union into the trade-side bits a feed
 * row needs. Uses the project's existing `isTokenTrade` / `isPerpTrade` /
 * `isPredictionTradeItem` guards — the same shape `Activity.tsx` consumes —
 * so a future trade-shape change updates every surface in one place. Mobile
 * rows ignore the perp chip fields today (no LONG/SHORT chip upgrade there);
 * web renders them.
 */
export function summarizeTradeItem(item) {
    if (isTokenTrade(item)) {
        return {
            isBuy: item.balanceImpact === BalanceImpact.POSITIVE,
            timestampMs: timestampToMs(item.timestamp),
            formattedTradeUsd: formatUsd(rawUsd(item.amount?.fiatValue?.())),
        };
    }
    if (isPerpTrade(item)) {
        return summarizePerpTrade(item);
    }
    if (isPredictionTradeItem(item)) {
        // Polymarket "price" is a 0–1 implied probability — render as ¢ when present.
        const prob = item.price ? Number(item.price) : NaN;
        const probText = Number.isFinite(prob) && prob > 0 ? `${Math.round(prob * 100)}¢` : "";
        const sideLabel = item.side === OrderSide.BUY ? "YES" : "NO";
        return {
            isBuy: item.balanceImpact === BalanceImpact.POSITIVE,
            timestampMs: timestampToMs(item.timestamp),
            formattedTradeUsd: formatUsd(rawUsdFromDecimal(item.fiatValue)),
            predictionTail: probText ? `on ${sideLabel} @ ${probText}` : `on ${sideLabel}`,
        };
    }
    return undefined;
}
/** Epoch ms a trade edge occurred at; undefined when the trade is undated. */
export function signalEdgeOccurredAtMs(edge) {
    return summarizeTradeItem(edge.tradeItem)?.timestampMs;
}
/** Feed-ordering timestamp — trade occurredAt or cluster lastEventAt. */
export function signalFeedItemTimestampMs(item) {
    return item.kind === SignalFeedItemKind.TRADE ? signalEdgeOccurredAtMs(item.edge) : item.cluster.lastEventAtMs;
}
/**
 * Newest-first feed ordering. Undated rows (trades whose timestamp failed
 * to parse) sort to the bottom explicitly instead of masquerading as
 * epoch-0 rows.
 */
export function compareSignalFeedItemsByTimestampDesc(a, b) {
    const aTs = signalFeedItemTimestampMs(a);
    const bTs = signalFeedItemTimestampMs(b);
    const aVal = notEmpty(aTs) ? aTs : Number.NEGATIVE_INFINITY;
    const bVal = notEmpty(bTs) ? bTs : Number.NEGATIVE_INFINITY;
    return bVal - aVal;
}
/** True for buy-side clusters (wire value "buy"). */
export function isSignalClusterBuy(cluster) {
    return cluster.side === OrderSide.BUY;
}
/**
 * Cluster aggregate notional as raw USDC-units bigint for `formatUsd`.
 * Display-only: `totalNotionalUsd` is a server-side Float aggregate, so
 * float→bigint via `Math.round` is fine below ~$9B of notional (2^53 µUSD)
 * — this value never feeds accounting math.
 */
export function clusterTotalUsdRaw(cluster) {
    return BigInt(Math.round(cluster.totalNotionalUsd * 1_000_000));
}
